import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { FieldMapService } from "@services/ingest/fieldMapService";
import { IngestDefinitionService } from "@services/ingest/ingestDefinitionService.ts";
import type { IngestConfigVersion } from "@models/configVersion.model";
import ConfigVersionsDrawer from "./ConfigVersionsDrawer";
import { foldDimensionValue, foldTargetsLine } from "./dimensionFold";
import HistoryButton from "@/shared/components/versions/HistoryButton";
import type {
  CoreFieldEntry,
  CoreLoadSpec,
  CoreTarget,
  EntityType,
  FieldMapDocument,
  FileColumns,
  IdentityAddress,
  IdentityContact,
  IdentityOrg,
  Metric,
  MetricType,
  Vertical,
} from "@models/fieldMap.model";
import {
  CatalogCoreFieldSample,
  DimensionValue,
  FieldCategory,
  FIELD_CATEGORY_LABEL,
  FIELD_CATEGORY_TOLERANCE,
} from "@models/catalog.model";
import { CatalogService } from "@services/catalog/catalogService";
import { Paths } from "@/paths";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";

const svc = new FieldMapService();
const catalogSvc = new CatalogService();

// The curated values of every dimension axis in a vertical, keyed by axis code. A pinned
// dimension literal is picked from these — shown by Name, stored as code — never typed:
// the literal is what the loader stores on every row, so a typed "Fall 2026" became a
// display name in the data that no rule could match (MPN-1663). An axis with no values
// offers nothing; the value is added to the catalog first.
async function loadDimensionValues(
  verticalCode: string | undefined,
): Promise<Record<string, DimensionValue[]>> {
  if (!verticalCode) return {};
  const dims = await catalogSvc.listDimensions(verticalCode).catch(() => []);
  const lists = await Promise.all(
    dims.map((d) =>
      catalogSvc
        .listDimensionValues(d.id)
        .then((values) => [d.code, values] as const)
        .catch(() => [d.code, [] as DimensionValue[]] as const),
    ),
  );
  return Object.fromEntries(lists);
}

const DEFAULT_VERTICAL_CODE = "education";

// Every Reach field map needs two email + two phone columns to match on the ad networks
// (MPN-1709), so a config that has never been configured starts with those four rows
// rather than four "+ Add contact" clicks. Seeding also fixes the Kind up front — hand
// entry has shipped phone columns saved as kind=email.
//
// The column starts empty (MPN-1967). Seeding "email"/"phone" meant an untouched default
// saved as a real mapping to a column no file had; an empty row is dropped on save.
const defaultContacts = (): ContactRow[] => [
  { sourceField: "", kind: "email", subtype: "" },
  { sourceField: "", kind: "email", subtype: "" },
  { sourceField: "", kind: "phone", subtype: "" },
  { sourceField: "", kind: "phone", subtype: "" },
];

// "Newly created" can't mean "no contacts": a config whose rows were all deleted on
// purpose would be re-seeded on the next load. It means the stored document is empty in
// every dimension — any save from this editor writes a vertical_code, so one save is
// enough to make this false for good.
const isUnconfigured = (doc: FieldMapDocument) =>
  doc.identity == null &&
  doc.vertical_code == null &&
  doc.core_load_specs.length === 0 &&
  doc.entity_load_specs.length === 0;
// Single reference width for the standalone inputs (vertical, consumer key, contact).
const FIELD_W = 280;

// Suggested contact labels (MPN-1217). NOT a fixed vocabulary — the field is freeform;
// these are just the common cross-channel axis (personal/work span email + phone) plus a
// vertical label for edu. Authors can type anything.
const SUBTYPE_SUGGESTIONS = ["personal", "work", "school"];

// ---- types ----

// How a row gets its dimension (MPN-2310). "column": the cell decides, folded through the
// catalog aliases at load ({"source": col}). "literal": one catalog code pinned on every row.
// "none": no dimension stored, so a rule on this axis never matches — an explicit choice, not
// the empty picker it used to be (ops read "Any" rendering as "Choose…" as a rejected pick).
type DimMode = "literal" | "column" | "none";
interface DimEntry {
  mode: DimMode;
  value: string;
}

interface GridRow {
  sourceField: string;
  entityTypeDefId: number; // which entity type this column maps to (per-row)
  metricId: number | null;
  dims: Record<string, DimEntry>; // keyed by the metric's dimension_keys
}

// One repeatable contact mapping (MPN-1217): a source column -> a consumer ContactPoint.
interface ContactRow {
  sourceField: string;
  kind: "email" | "phone";
  subtype: string; // freeform label; "" -> null on save
}

// One postal address, assembled from several columns (MPN-1706). Direct mail cannot reach
// anybody without one, and a client file spreads it over five or six columns.
interface AddressRow {
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// Address parts, in the order someone writes them. The keys are the server's own field names.
//
// Required is what the postal service needs to find a house (MPN-1746), not what the loader
// insists on: a record missing the city is stored and simply never mailed, while still being
// served on every other channel. Marking them here is what tells Ops which columns to chase
// before the file lands, rather than after a campaign under-delivers.
//
// Country is optional and blank means US. It is here rather than assumed because a client
// with any international records has no other way to say so.
const ADDRESS_PARTS: { key: keyof AddressRow; target: string; label: string; placeholder: string; required?: boolean }[] = [
  { key: "street", target: "address_1", label: "Street", placeholder: "e.g. address1", required: true },
  { key: "unit", target: "address_2", label: "Apt / Suite", placeholder: "e.g. address2" },
  { key: "city", target: "city", label: "City", placeholder: "e.g. city", required: true },
  { key: "state", target: "state", label: "State", placeholder: "e.g. state", required: true },
  { key: "postalCode", target: "postal_code", label: "ZIP", placeholder: "e.g. zip", required: true },
  { key: "country", target: "country_code", label: "Country", placeholder: "optional" },
];

// One repeatable CORE field mapping (MPN-1256): a source column -> a core-model field
// (account firmographic, person/consumer attr, or SalesLead attr) with a declared type.
interface CoreFieldRow {
  sourceField: string;
  targetField: string;
  valueTypeId: number | null;
}

// Structural / relational / contact-owned fields kept OUT of the core-field pickers:
// FKs and internal keys aren't user-mappable, and email/phone belong to the Contact section.
const STRUCTURAL_CORE_FIELDS = new Set([
  "person",
  "organization",
  "consumer",
  "external_id_hash",
  "fingerprint",
  "parent",
  "path",
  "email",
  "phone",
]);

// Holdout from the file (MPN-1667): old-platform augmented files carry a `treatment`
// column (Y = served, N = holdout). Opting in emits a consumer_population_segment core
// spec mapping it onto is_control — the loader inverts N -> control. Column name fixed:
// only the old platform produces it, so there's nothing for the user to pick.
const TREATMENT_COLUMN = "treatment";
const HOLDOUT_TARGET_FIELD = "is_control";
const CPS_TARGET_MODEL = "consumer_population_segment";

// "first_name" -> "First name" for the target-field dropdown labels (value stays raw).
const humanizeField = (f: string) =>
  f.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// ---- catalog helpers ----

// The metrics list endpoint is per entity type; we fetch every type in the
// vertical and tag each metric with its type so one flat pool serves all rows.
type CatalogMetric = Metric & { entityTypeDefId: number; entityName: string };

const fieldLabelOf = (m: CatalogMetric) => m.display_name || m.attr_key;

const nonReferenceTypes = (types: EntityType[], vertical: Vertical | undefined) =>
  vertical
    ? types
        .filter((t) => t.vertical_id === vertical.id && !t.is_reference)
        .sort((a, b) => Number(a.parent_type_id != null) - Number(b.parent_type_id != null))
    : [];

const rootTypeForVertical = (types: EntityType[], vertical: Vertical | undefined) =>
  nonReferenceTypes(types, vertical).find((t) => t.parent_type_id === null);

// Reconcile a row's dimension map against the selected metric's dimension_keys:
// keep values for keys that survive, drop stale keys, seed new keys empty.
function syncDims(
  prev: Record<string, DimEntry>,
  keys: string[],
): Record<string, DimEntry> {
  const out: Record<string, DimEntry> = {};
  keys.forEach((k) => {
    out[k] = prev[k] ?? { mode: "none", value: "" };
  });
  return out;
}

// ---- doc <-> form ----

function buildDimension(dims: Record<string, DimEntry>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, d] of Object.entries(dims)) {
    const v = d.value.trim();
    if (d.mode === "none" || !v) continue;
    out[key] = d.mode === "column" ? { source: v } : v;
  }
  return out;
}

// Valid core rows -> CoreFieldEntry[]; blank/incomplete rows are dropped on build.
function coreEntries(rows: CoreFieldRow[]): CoreFieldEntry[] {
  return rows
    .filter((r) => r.sourceField.trim() && r.targetField && r.valueTypeId != null)
    .map((r) => ({
      source_field: r.sourceField.trim(),
      target_field: r.targetField,
      value_type_id: r.valueTypeId as number,
    }));
}

interface BuildDocArgs {
  verticalCode: string;
  idCol: string;
  // The customer's name for the file-date column (MPN-1920). Blank saves the default.
  asOfCol: string;
  contactRows: ContactRow[];
  addressRows: AddressRow[];
  gridRows: GridRow[];
  entityTypes: EntityType[];
  // Extra key columns per child entity type (MPN-1818), comma-separated as typed.
  extraKeyCols: Record<number, string>;
  identityRows: CoreFieldRow[];
  orgKeyCol: string;
  orgRows: CoreFieldRow[];
  salesLeadRows: CoreFieldRow[];
  holdoutFromFile: boolean;
  // MetricType id with base_kind "string" — the treatment column's declared type.
  stringTypeId: number | null;
}

const splitColumns = (s: string) => s.split(",").map((c) => c.trim()).filter(Boolean);

function buildDoc(args: BuildDocArgs): FieldMapDocument {
  const { verticalCode, idCol, contactRows, addressRows, gridRows, entityTypes, extraKeyCols } = args;
  const key = idCol.trim();

  // Core key/contact doc copy (sample + description) is authored per vertical on the catalog
  // (MPN-1233), not per config — so this editor no longer emits field_meta.
  // Repeatable 1->M contacts (MPN-1217): one per non-blank column, order = row index.
  const contacts: IdentityContact[] = contactRows
    .filter((c) => c.sourceField.trim())
    .map((c, i) => ({
      source_field: c.sourceField.trim(),
      kind: c.kind,
      subtype: c.subtype.trim() || null,
      order: i,
    }));

  // Postal addresses (MPN-1706): one entry per row that names a street, because a row
  // without one cannot be mailed. The street column doubles as the entry's identity, the
  // same rule contacts use, so a corrected address updates rather than adding a second.
  const addresses: IdentityAddress[] = addressRows
    .filter((a) => a.street.trim())
    .map((a, i) => ({
      source_field: a.street.trim(),
      columns: Object.fromEntries(
        ADDRESS_PARTS.map((part) => [part.target, a[part.key].trim()]).filter(([, col]) => col),
      ),
      order: i,
    }));

  // Group mapped rows by entity type → one entity_load_spec per type. Root types
  // (no parent) key off the consumer key with no parent — that key is the join the
  // projection makes back to the Consumer. Non-root types parent off the consumer key
  // and key off it plus any extra key columns (MPN-1818): a file with one row per
  // student per term keys enrollment on student + term, so the duplicate-key check
  // sees a second term as a second record. Root specs sort first so children can
  // resolve their parent.
  const valid = gridRows.filter((r) => r.sourceField.trim() && r.metricId !== null && r.entityTypeDefId > 0);
  const byType = new Map<number, GridRow[]>();
  for (const r of valid) {
    const arr = byType.get(r.entityTypeDefId) ?? [];
    arr.push(r);
    byType.set(r.entityTypeDefId, arr);
  }

  const groups = [...byType.entries()].sort(([a], [b]) => {
    const rootA = entityTypes.find((t) => t.id === a)?.parent_type_id == null;
    const rootB = entityTypes.find((t) => t.id === b)?.parent_type_id == null;
    return Number(rootB) - Number(rootA);
  });

  const entity_load_specs = groups.map(([typeId, rows], seq) => {
    const isRoot = entityTypes.find((t) => t.id === typeId)?.parent_type_id == null;
    const extras = isRoot ? [] : splitColumns(extraKeyCols[typeId] ?? "");
    return {
      entity_type_def_id: typeId,
      natural_key_fields: key ? [key, ...extras] : [],
      parent_natural_key_fields: isRoot || !key ? [] : [key],
      sequence: seq,
      entries: rows.map((r) => ({
        source_field: r.sourceField.trim(),
        metric_id: r.metricId as number,
        dimension: buildDimension(r.dims),
      })),
    };
  });

  // B2B core sections (MPN-1256). All require the identity key server-side, so they're
  // only meaningful with a key: identity.entries + identity.organization hang off the
  // identity unit, and the SalesLead spec shares the consumer key.
  const identityEntries = key ? coreEntries(args.identityRows) : [];
  const orgKey = args.orgKeyCol.trim();
  const organization: IdentityOrg | null =
    key && orgKey ? { natural_key_fields: [orgKey], entries: coreEntries(args.orgRows) } : null;

  const salesEntries = key ? coreEntries(args.salesLeadRows) : [];
  const core_load_specs: CoreLoadSpec[] =
    salesEntries.length > 0
      ? [{ target_model: "sales_pipeline", natural_key_fields: [key], sequence: 0, entries: salesEntries }]
      : [];
  if (args.holdoutFromFile && key) {
    if (args.stringTypeId == null) throw new Error("Value types failed to load — cannot save holdout mapping");
    core_load_specs.push({
      target_model: CPS_TARGET_MODEL,
      natural_key_fields: [key],
      sequence: 0,
      entries: [{ source_field: TREATMENT_COLUMN, target_field: HOLDOUT_TARGET_FIELD, value_type_id: args.stringTypeId }],
    });
  }

  // Person + consumer (+org) are derived server-side from this one identity unit, so
  // "consumer without person" is unrepresentable. Emit identity when there's a key or
  // contacts; a config with neither is EAV-only (identity null). Entries/org above are
  // empty without a key, so they never force identity on their own.
  // An address counts as identity too: a config that only maps addresses is a mailing list.
  const hasIdentity = !!key || contacts.length > 0 || addresses.length > 0;
  return {
    vertical_code: verticalCode || null,
    as_of_column: args.asOfCol.trim() || "as_of",
    identity: hasIdentity
      ? {
          natural_key_fields: key ? [key] : [],
          entries: identityEntries,
          contacts,
          addresses,
          organization,
        }
      : null,
    core_load_specs,
    entity_load_specs,
    field_meta: [],
  };
}

interface ReversedCore {
  idCol: string;
  extraKeyCols: Record<number, string>;
  contacts: ContactRow[];
  addresses: AddressRow[];
  identityRows: CoreFieldRow[];
  orgKeyCol: string;
  orgRows: CoreFieldRow[];
  salesLeadRows: CoreFieldRow[];
  holdoutFromFile: boolean;
  storedEntityTypeDefId: number;
}

// CoreFieldEntry (doc) -> CoreFieldRow (form).
const toCoreRow = (e: CoreFieldEntry): CoreFieldRow => ({
  sourceField: e.source_field,
  targetField: e.target_field,
  valueTypeId: e.value_type_id,
});

function reverseCore(doc: FieldMapDocument): ReversedCore {
  const ident = doc.identity;
  const entity = doc.entity_load_specs[0];
  // This editor is new-path only; contacts are always the first-class identity.contacts.
  const contacts = [...(ident?.contacts ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((c) => ({
      sourceField: c.source_field,
      kind: c.kind,
      subtype: c.subtype ?? "",
    }));
  // Addresses back into rows, by the same column keys buildDoc emitted.
  const addresses: AddressRow[] = [...(ident?.addresses ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((a) => ({
      street: a.columns?.address_1 ?? "",
      unit: a.columns?.address_2 ?? "",
      city: a.columns?.city ?? "",
      state: a.columns?.state ?? "",
      postalCode: a.columns?.postal_code ?? "",
      country: a.columns?.country_code ?? "",
    }));
  const sales = doc.core_load_specs.find((s) => s.target_model === "sales_pipeline");
  const cps = doc.core_load_specs.find((s) => s.target_model === CPS_TARGET_MODEL);
  return {
    idCol: ident?.natural_key_fields?.[0] ?? "",
    // Everything after the consumer key is that type's extra key columns (MPN-1818).
    extraKeyCols: Object.fromEntries(
      doc.entity_load_specs.map((s) => [s.entity_type_def_id, s.natural_key_fields.slice(1).join(", ")]),
    ),
    contacts,
    addresses,
    identityRows: (ident?.entries ?? []).map(toCoreRow),
    orgKeyCol: ident?.organization?.natural_key_fields?.[0] ?? "",
    orgRows: (ident?.organization?.entries ?? []).map(toCoreRow),
    salesLeadRows: (sales?.entries ?? []).map(toCoreRow),
    holdoutFromFile: (cps?.entries ?? []).some((e) => e.target_field === HOLDOUT_TARGET_FIELD),
    // Any stored spec's type is enough to resolve which vertical to show.
    storedEntityTypeDefId: entity?.entity_type_def_id ?? 0,
  };
}

// Build grid rows from ALL stored entity specs (each row carries its entity
// type); dimension_keys of the resolved metric drive the dimension map.
function buildGridRows(
  specs: FieldMapDocument["entity_load_specs"],
  metrics: CatalogMetric[],
): GridRow[] {
  const rows: GridRow[] = [];
  for (const spec of specs) {
    for (const e of spec.entries) {
      const metric = metrics.find((m) => m.id === e.metric_id);
      const keys = metric?.dimension_keys ?? [];
      const dim = (e.dimension ?? {}) as Record<string, unknown>;
      const dims: Record<string, DimEntry> = {};
      keys.forEach((k) => {
        const val = dim[k];
        if (val && typeof val === "object" && "source" in (val as object))
          dims[k] = { mode: "column", value: String((val as { source: unknown }).source) };
        else if (val != null && String(val) !== "") dims[k] = { mode: "literal", value: String(val) };
        else dims[k] = { mode: "none", value: "" };
      });
      rows.push({
        sourceField: e.source_field,
        entityTypeDefId: spec.entity_type_def_id,
        metricId: e.metric_id,
        dims,
      });
    }
  }
  return rows;
}


// Category chip + its one-line data-tolerance explainer (MPN-1240). Copy lives in
// FIELD_CATEGORY_TOLERANCE so the editor and the instructions generator stay in sync.
function CategoryNote({ category }: { category: FieldCategory }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        gap: 1,
        mb: 1.5,
        p: 1,
        borderRadius: 1,
        bgcolor: "#f6f4fd",
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: 12, color: "#6e44c8" }}>
        {FIELD_CATEGORY_LABEL[category]}
      </Typography>
      <Typography sx={{ color: "#8c9097", fontSize: 12 }}>
        {FIELD_CATEGORY_TOLERANCE[category]}
      </Typography>
    </Box>
  );
}

// Title + helper on one line to save vertical space.
function SectionHead({ title, help }: { title: string; help: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      <Typography sx={{ color: "#8c9097", fontSize: 13 }}>{help}</Typography>
    </Box>
  );
}

// Read-only view of a core field's per-vertical doc-copy (MPN-1233). The sample + description
// are authored on the Tracked Metric Catalog, not per config — so we show, never edit, them here.
function CoreSampleReadOnly({ label, sample }: { label: string; sample?: CatalogCoreFieldSample }) {
  return (
    <Box sx={{ fontSize: 12, color: "#8c9097" }}>
      {sample && (sample.sample || sample.description) ? (
        <>
          <Box component="span">{label}: </Box>
          {sample.sample && (
            <Box component="span" sx={{ fontFamily: "monospace", color: "#555" }}>{sample.sample}</Box>
          )}
          {sample.description && <Box component="span"> — {sample.description}</Box>}
          <Box component="span" sx={{ fontStyle: "italic" }}> · set in catalog</Box>
        </>
      ) : (
        <Box component="span" sx={{ fontStyle: "italic" }}>
          No {label.toLowerCase()} example yet — add one on the Tracked Metric Catalog.
        </Box>
      )}
    </Box>
  );
}

// Repeatable core-field rows (MPN-1256): source column -> a core-model field + value type.
// Controlled — the parent owns the array. Reused by the account, person/consumer and
// SalesLead sections; the target-field options differ per section.
function CoreFieldRows({
  rows,
  onChange,
  targetOptions,
  metricTypes,
  columnLabel = "Customer column",
  fieldLabel = "Maps to",
  addLabel = "+ Add field",
}: {
  rows: CoreFieldRow[];
  onChange: (rows: CoreFieldRow[]) => void;
  targetOptions: string[];
  metricTypes: MetricType[];
  columnLabel?: string;
  fieldLabel?: string;
  addLabel?: string;
}) {
  const add = () =>
    onChange([...rows, { sourceField: "", targetField: "", valueTypeId: metricTypes[0]?.id ?? null }]);
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const change = (i: number, patch: Partial<CoreFieldRow>) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  return (
    <>
      {rows.length > 0 && (
        // Scroll instead of clip below the table's min width (keeps the trash column reachable).
        <Box sx={{ overflowX: "auto", mb: 1.5 }}>
          <Box
            component="table"
            sx={{
              width: "100%",
              minWidth: 620,
              tableLayout: "fixed",
              borderCollapse: "collapse",
              "& th": {
                textAlign: "left",
                fontSize: 12,
                fontWeight: 600,
                color: "#8c9097",
                pb: 1,
                pr: 2,
                borderBottom: "1px solid #e9ecef",
              },
              "& td": { pt: 1.5, pr: 2, verticalAlign: "middle" },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: "34%" }}>{columnLabel}</th>
                <th style={{ width: "34%" }}>{fieldLabel}</th>
                <th>Type</th>
                <th style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <TextField
                      size="small"
                      value={r.sourceField}
                      onChange={(e) => change(i, { sourceField: e.target.value })}
                      placeholder="e.g. account_name"
                      sx={{ width: "100%" }}
                      inputProps={{ "aria-label": `${columnLabel} ${i + 1}` }}
                    />
                  </td>
                  <td>
                    <FormControl size="small" sx={{ width: "100%" }}>
                      <Select
                        displayEmpty
                        value={r.targetField || ""}
                        onChange={(e) => change(i, { targetField: String(e.target.value) })}
                        renderValue={(v) =>
                          v ? humanizeField(String(v)) : <span style={{ color: "#8c9097" }}>Select field…</span>
                        }
                        inputProps={{ "aria-label": `${fieldLabel} ${i + 1}` }}
                      >
                        <MenuItem value="">
                          <em style={{ color: "#8c9097" }}>Select field…</em>
                        </MenuItem>
                        {targetOptions.map((f) => (
                          <MenuItem key={f} value={f}>
                            {humanizeField(f)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </td>
                  <td>
                    <FormControl size="small" sx={{ width: "100%" }}>
                      <Select
                        displayEmpty
                        value={r.valueTypeId == null ? "" : String(r.valueTypeId)}
                        onChange={(e) =>
                          change(i, {
                            valueTypeId: e.target.value === "" ? null : Number(e.target.value),
                          })
                        }
                        renderValue={(v) => {
                          if (!v) return <span style={{ color: "#8c9097" }}>Type…</span>;
                          return metricTypes.find((t) => String(t.id) === v)?.name ?? String(v);
                        }}
                        inputProps={{ "aria-label": `Type ${i + 1}` }}
                      >
                        {metricTypes.map((t) => (
                          <MenuItem key={t.id} value={String(t.id)}>
                            {t.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </td>
                  <td>
                    <IconButton size="small" onClick={() => remove(i)} title="Remove field">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={add}
        sx={{ textTransform: "none", color: "#845adf" }}
      >
        {addLabel}
      </Button>
    </>
  );
}

// Searchable Tracked Field selector (MPN-1189 direction). One control replaces the old
// Entity → field cascade: the whole vertical's field pool is searchable at once, entity is
// derived from the pick (shown as a tag) and offered as an optional filter — chips in the
// popover header plus entity group headers in the "All" view.
type FieldOption = CatalogMetric & { label: string };

function FieldSelect({
  options,
  value,
  onSelect,
  onCreateNew,
  index,
}: {
  options: CatalogMetric[];
  value: number | null;
  onSelect: (metric: CatalogMetric | null) => void;
  onCreateNew: () => void;
  index: number;
}) {
  // null == "All"; otherwise restrict the list to one entity type.
  const [entityFilter, setEntityFilter] = useState<number | null>(null);

  const opts: FieldOption[] = useMemo(
    () => options.map((m) => ({ ...m, label: fieldLabelOf(m) })),
    [options],
  );
  // Distinct entities present in the pool, in first-seen order (pool is already grouped by type).
  const entities = useMemo(() => {
    const seen = new Map<number, string>();
    opts.forEach((o) => {
      if (!seen.has(o.entityTypeDefId)) seen.set(o.entityTypeDefId, o.entityName);
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [opts]);

  const shown = entityFilter == null ? opts : opts.filter((o) => o.entityTypeDefId === entityFilter);
  const selected = opts.find((o) => o.id === value) ?? null;

  // Custom popover: entity filter chips (header) + the option list + a "create new" footer.
  // Memoized so typing (which doesn't touch these deps) never remounts the listbox.
  const PaperComponent = useMemo(() => {
    const Wrapped = (paperProps: React.HTMLAttributes<HTMLElement>) => (
      <Paper {...paperProps}>
        {entities.length > 1 && (
          // preventDefault on mousedown keeps input focus so the popover stays open on chip click.
          <Box onMouseDown={(e) => e.preventDefault()} sx={{ p: 1, borderBottom: "1px solid #e9ecef" }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: "#8c9097",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 0.75,
              }}
            >
              Filter by entity
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              <Chip
                label="All"
                size="small"
                onClick={() => setEntityFilter(null)}
                color={entityFilter == null ? "primary" : "default"}
                variant={entityFilter == null ? "filled" : "outlined"}
              />
              {entities.map((e) => (
                <Chip
                  key={e.id}
                  label={e.name}
                  size="small"
                  onClick={() => setEntityFilter(e.id)}
                  color={entityFilter === e.id ? "primary" : "default"}
                  variant={entityFilter === e.id ? "filled" : "outlined"}
                />
              ))}
            </Box>
          </Box>
        )}
        {paperProps.children}
        <Box onMouseDown={(e) => e.preventDefault()} sx={{ borderTop: "1px solid #e9ecef" }}>
          <Button
            fullWidth
            size="small"
            startIcon={<AddIcon />}
            onClick={onCreateNew}
            sx={{ justifyContent: "flex-start", textTransform: "none", color: "#845adf", px: 2, py: 1 }}
          >
            Create new tracked field
          </Button>
        </Box>
      </Paper>
    );
    return Wrapped;
  }, [entities, entityFilter, onCreateNew]);

  return (
    <Autocomplete
      size="small"
      sx={{ width: "100%" }}
      // Keyboard-first: highlight the top match as you type (Enter confirms it), and commit the
      // highlighted option on blur — tabbing out or clicking away selects it, no extra click.
      autoHighlight
      autoSelect
      options={shown}
      value={selected}
      onChange={(_e, val) => onSelect(val)}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      // Group headers surface the entity in the unfiltered view; redundant once a chip narrows it.
      groupBy={entityFilter == null ? (o) => o.entityName : undefined}
      PaperComponent={PaperComponent}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id}>
          {o.label}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search fields…"
          inputProps={{ ...params.inputProps, "aria-label": `Tracked field ${index + 1}` }}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected ? (
              <Chip size="small" label={selected.entityName} sx={{ mr: 0.5 }} />
            ) : (
              params.InputProps.startAdornment
            ),
          }}
        />
      )}
    />
  );
}

// A customer column held to the latest file's header (MPN-1967). Chamberlain typed
// 'Email Address' against a file headed 'EmailAddress' and published empty; with a header
// on hand the only choices are the file's own names. Free text only before any file
// exists — the section says those names are unverified. A saved name the file no longer
// has stays visible, flagged, so it can be re-picked rather than silently blanked.
// "Try a value": type what a cell might hold and see the catalog value it folds to, before any
// file is run (MPN-2310). Read-only; nothing here reaches the saved field map.
function TryDimensionValue({ axis, options }: { axis: string; options: DimensionValue[] }) {
  const [raw, setRaw] = useState("");
  const hit = foldDimensionValue(raw, options);
  const tried = raw.trim() !== "";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
      <TextField
        size="small"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder="Try a value"
        sx={{ width: 160 }}
        inputProps={{ "aria-label": `Try a ${axis} value` }}
      />
      {tried && (
        <Typography sx={{ fontSize: 12, color: hit ? "#2e7d32" : "#c62828" }}>
          {hit ? `→ ${hit.name || hit.code}` : "→ no match: stored as-is, matches no rule"}
        </Typography>
      )}
    </Box>
  );
}

function FileColumnInput({
  value,
  onChange,
  header,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  header: string[];
  placeholder: string;
  ariaLabel: string;
}) {
  if (header.length === 0) {
    return (
      <TextField
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        sx={{ width: "100%" }}
        inputProps={{ "aria-label": ariaLabel }}
      />
    );
  }
  const missing = !!value && !header.includes(value);
  return (
    <Autocomplete
      size="small"
      options={missing ? [value, ...header] : header}
      value={value || null}
      onChange={(_e, v) => onChange(v ?? "")}
      renderInput={(params) => (
        <TextField
          {...params}
          error={missing}
          helperText={missing ? "Not in the latest file" : undefined}
          placeholder="Pick a column"
          inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
        />
      )}
      sx={{ width: "100%" }}
    />
  );
}

// Where the column choices come from, under the contact and address sections (MPN-1967).
function FileColumnsNote({ file }: { file: FileColumns }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
      {file.columns.length > 0
        ? `Columns from the latest file: ${file.file_name ?? "unnamed"}${file.as_of ? ` (${file.as_of})` : ""}.`
        : "No file yet — these column names are unverified until the first file is checked."}
    </Typography>
  );
}

// ---- Editor ----

interface FieldMapEditorProps {
  definitionId: number;
  /** Back affordance in the header. Omitted → no back link (embedded hosts own their own). */
  onBack?: () => void;
  backLabel?: string;
  /** Export instructions. Omitted → the button is hidden. */
  onOpenInstructions?: () => void;
}

export function FieldMapEditor({
  definitionId,
  onBack,
  backLabel = "Back to Files",
  onOpenInstructions,
}: FieldMapEditorProps) {

  // --- catalog data ---
  const [verticals, setVerticals] = useState<Vertical[]>([]);
  const [allEntityTypes, setAllEntityTypes] = useState<EntityType[]>([]);
  // Flat pool of every mappable metric in the vertical, tagged with its entity type.
  const [metrics, setMetrics] = useState<CatalogMetric[]>([]);
  // Curated dimension values per axis for the current vertical (MPN-1663).
  const [dimValues, setDimValues] = useState<Record<string, DimensionValue[]>>({});
  // Core targets (person/consumer/organization/sales_pipeline + assignable_fields) and the
  // value-type pool — both vertical-independent, so loaded once on mount (MPN-1256).
  const [coreTargets, setCoreTargets] = useState<CoreTarget[]>([]);
  const [metricTypes, setMetricTypes] = useState<MetricType[]>([]);

  // --- form state ---
  const [verticalCode, setVerticalCode] = useState("");
  const [idCol, setIdCol] = useState("");
  const [asOfCol, setAsOfCol] = useState("as_of");
  const [extraKeyCols, setExtraKeyCols] = useState<Record<number, string>>({});
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [addresses, setAddresses] = useState<AddressRow[]>([]);
  // The latest file's header (MPN-1967): contact and address columns are picked from it.
  const [fileColumns, setFileColumns] = useState<FileColumns>({ columns: [] });
  const [gridRows, setGridRows] = useState<GridRow[]>([]);
  // B2B core sections (MPN-1256): account firmographics + match key, person/consumer
  // attrs, and downstream SalesLead attrs. Not vertical-scoped (fixed core models).
  const [identityRows, setIdentityRows] = useState<CoreFieldRow[]>([]);
  const [orgKeyCol, setOrgKeyCol] = useState("");
  const [orgRows, setOrgRows] = useState<CoreFieldRow[]>([]);
  const [salesLeadRows, setSalesLeadRows] = useState<CoreFieldRow[]>([]);
  const [holdoutFromFile, setHoldoutFromFile] = useState(false);
  // Per-vertical doc-copy for the core key/contact fields (MPN-1233), shown read-only here.
  const [coreSamples, setCoreSamples] = useState<CatalogCoreFieldSample[]>([]);
  const sampleFor = (role: string) => coreSamples.find((s) => s.role === role);

  // --- UI state ---
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [versions, setVersions] = useState<IngestConfigVersion[]>([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [definitionName, setDefinitionName] = useState("");

  const loadVersions = async () => {
    if (!definitionId) return;
    const defSvc = new IngestDefinitionService();
    const [defs, vers] = await Promise.all([
      defSvc.fetchDefinitions(),
      defSvc.listConfigVersions(definitionId),
    ]);
    setVersions(vers);
    const def = defs.find((d: { id: number; name: string }) => d.id === definitionId);
    if (def) setDefinitionName(def.name);
  };

  useEffect(() => {
    loadVersions().catch(() => {});
     
  }, [definitionId]);

  const currentVertical = verticals.find((v) => v.code === verticalCode);
  // One flat, searchable pool of every mappable field in the vertical.
  // Entity is derived from the pick, not chosen up front.
  const allFieldOptions = metrics;

  // Mappable core fields per target model (structural/relational/contact-owned removed).
  const fieldsFor = (model: string) =>
    (coreTargets.find((t) => t.value === model)?.assignable_fields ?? []).filter(
      (f) => !STRUCTURAL_CORE_FIELDS.has(f),
    );
  const accountOptions = fieldsFor("organization");
  const personConsumerOptions = [...new Set([...fieldsFor("person"), ...fieldsFor("consumer")])];
  const salesLeadOptions = fieldsFor("sales_pipeline");

  // Fetch metrics for every non-reference type in the vertical, tagged by type.
  const loadVerticalMetrics = async (
    vertical: Vertical | undefined,
    types: EntityType[],
  ): Promise<CatalogMetric[]> => {
    const lists = await Promise.all(
      nonReferenceTypes(types, vertical).map((t) =>
        svc
          .listMetrics(t.code)
          .then((ms) => ms.map((m) => ({ ...m, entityTypeDefId: t.id, entityName: t.name })))
          .catch(() => [] as CatalogMetric[]),
      ),
    );
    return lists.flat();
  };

  // Mount: load field map + catalog in parallel, resolve vertical, then metrics + grid.
  useEffect(() => {
    if (!definitionId) return;
    (async () => {
      try {
        const [fieldMap, vs, types, cts, mts, fc] = await Promise.all([
          svc.getFieldMap(definitionId),
          svc.listVerticals(),
          svc.listEntityTypes(),
          svc.listCoreTargets().catch(() => [] as CoreTarget[]),
          svc.listMetricTypes().catch(() => [] as MetricType[]),
          svc.getFileColumns(definitionId).catch((): FileColumns => ({ columns: [] })),
        ]);
        setFileColumns(fc);
        setWarnings(fieldMap.warnings ?? []);
        setVerticals(vs);
        setAllEntityTypes(types);
        setCoreTargets(cts);
        setMetricTypes(mts);

        const reversed = reverseCore(fieldMap);
        setIdCol(reversed.idCol);
        setAsOfCol(fieldMap.as_of_column || "as_of");
        setExtraKeyCols(reversed.extraKeyCols);
        setContacts(isUnconfigured(fieldMap) ? defaultContacts() : reversed.contacts);
        // Addresses are not seeded the way contacts are. Direct mail is opt-in per client,
        // and prefilled address rows would read as "every config maps an address".
        setAddresses(reversed.addresses);
        setIdentityRows(reversed.identityRows);
        setOrgKeyCol(reversed.orgKeyCol);
        setOrgRows(reversed.orgRows);
        setSalesLeadRows(reversed.salesLeadRows);
        setHoldoutFromFile(reversed.holdoutFromFile);

        // Resolve the vertical: persisted vertical_code first (MPN-1233), then a stored
        // entity type's vertical, then default EDU.
        const savedVertical = fieldMap.vertical_code
          ? vs.find((v) => v.code === fieldMap.vertical_code)
          : undefined;
        const storedType = types.find((t) => t.id === reversed.storedEntityTypeDefId);
        const storedVertical = storedType && vs.find((v) => v.id === storedType.vertical_id);
        const vertical =
          savedVertical ?? storedVertical ?? vs.find((v) => v.code === DEFAULT_VERTICAL_CODE) ?? vs[0];
        setVerticalCode(vertical?.code ?? "");

        const [ms, dvs] = await Promise.all([
          loadVerticalMetrics(vertical, types),
          loadDimensionValues(vertical?.code),
        ]);
        setMetrics(ms);
        setDimValues(dvs);
        setGridRows(buildGridRows(fieldMap.entity_load_specs ?? [], ms));
        if (vertical) setCoreSamples(await svc.listCoreFieldSamples(vertical.code).catch(() => []));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load field map");
      } finally {
        setLoading(false);
      }
    })();
  }, [definitionId]);

  const handleVerticalChange = async (code: string) => {
    setVerticalCode(code);
    setGridRows([]); // rows' entity/metric ids belong to the previous vertical
    const vertical = verticals.find((v) => v.code === code);
    const [ms, cfs, dvs] = await Promise.all([
      loadVerticalMetrics(vertical, allEntityTypes),
      vertical ? svc.listCoreFieldSamples(vertical.code).catch(() => []) : Promise.resolve([]),
      loadDimensionValues(vertical?.code),
    ]);
    setMetrics(ms);
    setCoreSamples(cfs);
    setDimValues(dvs);
  };

  // --- contact row helpers ---
  const addContact = () =>
    setContacts((prev) => [...prev, { sourceField: "", kind: "email", subtype: "" }]);
  const removeContact = (i: number) =>
    setContacts((prev) => prev.filter((_, idx) => idx !== i));
  const changeContact = (i: number, patch: Partial<ContactRow>) =>
    setContacts((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });

  // --- address row helpers (MPN-1706) ---
  const addAddress = () =>
    setAddresses((prev) => [
      ...prev,
      { street: "", unit: "", city: "", state: "", postalCode: "", country: "" },
    ]);
  const removeAddress = (i: number) =>
    setAddresses((prev) => prev.filter((_, idx) => idx !== i));
  const changeAddress = (i: number, patch: Partial<AddressRow>) =>
    setAddresses((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });

  // --- grid row helpers ---
  const addGridRow = () =>
    setGridRows((prev) => [
      ...prev,
      {
        sourceField: "",
        entityTypeDefId: rootTypeForVertical(allEntityTypes, currentVertical)?.id ?? 0,
        metricId: null,
        dims: {},
      },
    ]);

  const removeGridRow = (i: number) => setGridRows((prev) => prev.filter((_, idx) => idx !== i));

  const changeSource = (i: number, source: string) =>
    setGridRows((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], sourceField: source };
      return next;
    });

  // Picking a field sets its metric AND derives the row's entity from that field; clearing it
  // (metric null) drops the field and dimensions but leaves the entity as a harmless placeholder
  // (blank rows are filtered out on save).
  const selectField = (i: number, metric: CatalogMetric | null) =>
    setGridRows((prev) => {
      const next = [...prev];
      const cur = next[i];
      next[i] = metric
        ? {
            ...cur,
            metricId: metric.id,
            entityTypeDefId: metric.entityTypeDefId,
            dims: syncDims(cur.dims, metric.dimension_keys ?? []),
          }
        : { ...cur, metricId: null, dims: {} };
      return next;
    });

  const changeDim = (i: number, key: string, patch: Partial<DimEntry>) =>
    setGridRows((prev) => {
      const next = [...prev];
      const cur = next[i];
      next[i] = { ...cur, dims: { ...cur.dims, [key]: { ...cur.dims[key], ...patch } } };
      return next;
    });

  // --- save ---
  // Entity types the grid maps, roots first; the key section lists one row each (MPN-1818).
  const keyedTypes = useMemo(() => {
    const used = new Set(gridRows.map((r) => r.entityTypeDefId).filter((id) => id > 0));
    return allEntityTypes
      .filter((t) => used.has(t.id))
      .sort((a, b) => Number(a.parent_type_id != null) - Number(b.parent_type_id != null));
  }, [gridRows, allEntityTypes]);

  const handleSave = async () => {
    // Contacts hang off the consumer spec, which the server requires a natural key for
    // ("identity has empty natural_key_fields", 400). Say so here instead of shipping a
    // payload that can't validate — MPN-1709's four default rows mean a brand-new map
    // arrives at Save already holding contacts.
    if (!idCol.trim() && contacts.some((c) => c.sourceField.trim())) {
      setError("Add the Key column before saving — contact columns are stored against it.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const doc = buildDoc({
        verticalCode,
        idCol,
        asOfCol,
        contactRows: contacts,
        addressRows: addresses,
        gridRows,
        entityTypes: allEntityTypes,
        extraKeyCols,
        identityRows,
        orgKeyCol,
        orgRows,
        salesLeadRows,
        holdoutFromFile,
        stringTypeId: metricTypes.find((t) => t.base_kind === "string")?.id ?? null,
      });
      const saved = await svc.saveFieldMap(definitionId, doc);
      setWarnings(saved.warnings ?? []);
      const reversed = reverseCore(saved);
      setIdCol(reversed.idCol);
      setAsOfCol(saved.as_of_column || "as_of");
      setExtraKeyCols(reversed.extraKeyCols);
      setContacts(reversed.contacts);
      setAddresses(reversed.addresses);
      setIdentityRows(reversed.identityRows);
      setOrgKeyCol(reversed.orgKeyCol);
      setOrgRows(reversed.orgRows);
      setSalesLeadRows(reversed.salesLeadRows);
      setHoldoutFromFile(reversed.holdoutFromFile);
      setGridRows(buildGridRows(saved.entity_load_specs ?? [], metrics));
      setSavedAt(Date.now());
      loadVersions().catch(() => {});
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // One Map-fields row. Extracted so the grouped render (by category) can call it while
  // keeping each row's original gridRows index for the index-based change/remove helpers.
  const renderGridRow = (row: GridRow, i: number) => {
    const metric = metrics.find((m) => m.id === row.metricId);
    const dimKeys = metric?.dimension_keys ?? [];
    return (
      <tr key={i}>
        <td>
          <TextField
            size="small"
            value={row.sourceField}
            onChange={(e) => changeSource(i, e.target.value)}
            placeholder="e.g. is_registered_1"
            sx={{ width: "100%" }}
            inputProps={{ "aria-label": "Customer Column" }}
          />
        </td>
        <td>
          <FieldSelect
            options={allFieldOptions}
            value={row.metricId}
            onSelect={(m) => selectField(i, m)}
            onCreateNew={() => setInfo("Creating new tracked fields is coming soon (MPN-1189).")}
            index={i}
          />
          {/* EAV description/sample come from the catalog metric (read-only here). */}
          {metric && (metric.description || metric.sample) && (
            <Typography sx={{ color: "#8c9097", fontSize: 11, mt: 0.5, maxWidth: "100%" }}>
              {metric.description}
              {metric.sample ? ` · e.g. ${metric.sample}` : ""}
            </Typography>
          )}
        </td>
        <td>
          {dimKeys.length === 0 ? (
            <Typography sx={{ color: "#c2c6cc", fontSize: 13, pt: 1 }}>—</Typography>
          ) : (
            dimKeys.map((key) => {
              const d = row.dims[key] ?? { mode: "none", value: "" };
              const options = dimValues[key] ?? [];
              // A stored literal the catalog does not know (authored before the picker) stays
              // visible and flagged; the server refuses the save until a catalog value is picked.
              const unknownStored = d.mode === "literal" && d.value && !options.some((o) => o.code === d.value);
              return (
                <Box key={key} sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "#5c6066", mb: 0.5 }}>{key}</Typography>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    {/* Mode first, so the row reads as a sentence: "<axis> from column <col>",
                        "<axis> always <value>", "<axis> not set". A mode switch clears the value:
                        a pinned code is not a column name and vice versa. */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={d.mode}
                        onChange={(e) => changeDim(i, key, { mode: e.target.value as DimMode, value: "" })}
                        inputProps={{ "aria-label": `Dimension ${key} mode` }}
                      >
                        <MenuItem value="column">from column</MenuItem>
                        <MenuItem value="literal">always</MenuItem>
                        <MenuItem value="none">not set</MenuItem>
                      </Select>
                    </FormControl>
                    {d.mode === "column" && (
                      <Box sx={{ flex: 1, minWidth: 160 }}>
                        <FileColumnInput
                          value={d.value}
                          onChange={(v) => changeDim(i, key, { value: v })}
                          header={fileColumns.columns}
                          placeholder="column name"
                          ariaLabel={`Dimension ${key}`}
                        />
                        <Typography sx={{ color: "#8c9097", fontSize: 11, mt: 0.5 }}>
                          {foldTargetsLine(key, options)}
                        </Typography>
                        {options.length > 0 && <TryDimensionValue axis={key} options={options} />}
                      </Box>
                    )}
                    {d.mode === "literal" && (
                      <Box sx={{ flex: 1, minWidth: 160 }}>
                        <FormControl size="small" fullWidth disabled={options.length === 0 && !unknownStored}>
                          <Select
                            value={d.value}
                            displayEmpty
                            error={Boolean(unknownStored)}
                            onChange={(e) => changeDim(i, key, { value: e.target.value as string })}
                            inputProps={{ "aria-label": `Dimension ${key}` }}
                            renderValue={(v) => {
                              if (!v) return <em>{options.length ? `Choose ${key}` : `No ${key} values yet`}</em>;
                              const match = options.find((o) => o.code === v);
                              return match ? match.name || match.code : `${v} — not in catalog`;
                            }}
                          >
                            {unknownStored && (
                              <MenuItem value={d.value} disabled>
                                {d.value} — not in catalog
                              </MenuItem>
                            )}
                            {options.map((o) => (
                              <MenuItem key={o.code} value={o.code}>
                                {o.name || o.code}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography sx={{ color: "#8c9097", fontSize: 11, mt: 0.5 }}>
                          {options.length === 0 ? (
                            <>
                              The catalog has no {key} values yet. <Link to={Paths.CATALOG}>Add them</Link>, then pick one here.
                            </>
                          ) : (
                            `Every row from this column gets this ${key}.`
                          )}
                        </Typography>
                      </Box>
                    )}
                    {d.mode === "none" && (
                      <Typography sx={{ color: "#8c9097", fontSize: 12, pt: 1 }}>
                        No {key} stored. A rule on {key} will never match these rows.
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </td>
        <td>
          {/* Cells are top-aligned so multi-dimension rows keep their inputs at the top;
              center the trash within one input-row height so it lines up with the controls. */}
          <Box sx={{ display: "flex", alignItems: "center", height: 40 }}>
            <IconButton size="small" onClick={() => removeGridRow(i)} title="Remove row">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography sx={{ color: "#8c9097" }}>Loading field map…</Typography>
      </Box>
    );
  }

  const sectionSx = { p: 3, mb: 3, width: "100%", boxSizing: "border-box" } as const;

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {onBack && (
            <>
              <Box
                component="a"
                onClick={onBack}
                sx={{
                  color: "#845adf",
                  cursor: "pointer",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: 14,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 12, mr: 0.5 }} />
                {backLabel}
              </Box>
              <Typography sx={{ color: "#8c9097", mx: 1 }}>&middot;</Typography>
            </>
          )}
          <Typography variant="h6" fontWeight={600}>
            Field Map
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {savedAt && (
            <Typography sx={{ color: "#26bf94", fontSize: 13 }}>
              Saved {new Date(savedAt).toLocaleTimeString()}
            </Typography>
          )}
          {/* "Versions", not "History": that is the word this page's drawer, its runs and its
              pinning have always used. The count is free here because the list loads with the
              page, unlike the surfaces that fetch on open. */}
          <HistoryButton
            onClick={() => setVersionsOpen(true)}
            label="Versions"
            title="Field map versions"
            count={versions.length}
          />
          {onOpenInstructions && (
            <Button
              variant="outlined"
              disabled={saving}
              onClick={onOpenInstructions}
              sx={{ textTransform: "none", color: "#845adf", borderColor: "#845adf" }}
            >
              Export instructions
            </Button>
          )}
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ textTransform: "none", bgcolor: "#845adf", "&:hover": { bgcolor: "#6e44c8" } }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {warnings.length > 0 && (
        // Another population's config writes the same tracked metric cell (MPN-2059). Tracked
        // data is per org, so the fix is a literal Metric Dimension on each mapping, not a
        // correction. Not dismissable: it is about the saved config, not this session.
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Shares a tracked metric cell with another population</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}
      {saving && <LinearProgress sx={{ mb: 3 }} />}

      {/* Vertical */}
      <Paper variant="outlined" sx={sectionSx}>
        <SectionHead
          title="Choose a vertical"
          help="Defaults to Education (EDU). Pick each column's entity type per row below."
        />
        <FormControl size="small" sx={{ width: FIELD_W }}>
          <Select
            value={verticalCode || ""}
            onChange={(e) => void handleVerticalChange(String(e.target.value))}
            displayEmpty
            renderValue={(v) => {
              if (!v) return <span style={{ color: "#8c9097" }}>Select vertical…</span>;
              return verticals.find((x) => x.code === v)?.name ?? String(v);
            }}
          >
            {verticals.map((v) => (
              <MenuItem key={v.code} value={v.code}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Consumer key — System & keys category */}
      <Paper variant="outlined" sx={sectionSx}>
        <CategoryNote category="system" />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField
            size="small"
            label="Key column"
            value={idCol}
            onChange={(e) => setIdCol(e.target.value)}
            placeholder="e.g. student_id"
            sx={{ width: FIELD_W }}
          />
          {/* The file's snapshot date, in the customer's own column name (MPN-1920).
              Matched exactly: Butler sends AS_OF_DATE. */}
          <TextField
            size="small"
            label="File date column"
            value={asOfCol}
            onChange={(e) => setAsOfCol(e.target.value)}
            placeholder="as_of"
            helperText="The column holding the file's as_of date, exactly as it appears in the file."
            sx={{ width: FIELD_W }}
          />
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <CoreSampleReadOnly label="Sample" sample={sampleFor("key")} />
        </Box>
        {keyedTypes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Row key per entity type</Typography>
            {keyedTypes.map((t) => (
              <Box key={t.id} sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                <Typography sx={{ width: 140, fontSize: 14 }}>{t.name}</Typography>
                <Typography sx={{ fontSize: 14 }}>{idCol.trim() || "Key column"}</Typography>
                {t.parent_type_id != null && (
                  <>
                    <Typography sx={{ fontSize: 14 }}>+</Typography>
                    <TextField
                      size="small"
                      label={`${t.name} key columns`}
                      value={extraKeyCols[t.id] ?? ""}
                      onChange={(e) => setExtraKeyCols({ ...extraKeyCols, [t.id]: e.target.value })}
                      placeholder="e.g. Term"
                      sx={{ width: FIELD_W }}
                    />
                  </>
                )}
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Add a column when the file has more than one row per person for that type. Comma-separate for more than one.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Contact info — repeatable 1->M ContactPoints (MPN-1217) */}
      <Paper variant="outlined" sx={sectionSx}>
        <CategoryNote category="contact" />
        <Typography sx={{ color: "#8c9097", fontSize: 13, mb: 1.5 }}>
          One row per email/phone column — add as many as the file has. Label is optional.
        </Typography>
        <FileColumnsNote file={fileColumns} />
        {contacts.length > 0 && (
          // Scroll instead of clip below the table's min width (keeps the trash column reachable).
          <Box sx={{ overflowX: "auto", mb: 1.5 }}>
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 520,
                tableLayout: "fixed",
                borderCollapse: "collapse",
                "& th": {
                  textAlign: "left",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#8c9097",
                  pb: 1,
                  pr: 2,
                  borderBottom: "1px solid #e9ecef",
                },
                "& td": { pt: 1.5, pr: 2, verticalAlign: "middle" },
              }}
            >
              <thead>
                <tr>
                  <th>Customer column</th>
                  <th style={{ width: "22%" }}>Kind</th>
                  <th style={{ width: "30%" }}>Label (optional)</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {contacts.map((c, i) => (
                  <tr key={i}>
                    <td>
                      <FileColumnInput
                        value={c.sourceField}
                        onChange={(v) => changeContact(i, { sourceField: v })}
                        header={fileColumns.columns}
                        placeholder="e.g. email1"
                        ariaLabel={`Contact column ${i + 1}`}
                      />
                    </td>
                    <td>
                      <FormControl size="small" sx={{ width: "100%" }}>
                        <Select
                          value={c.kind}
                          onChange={(e) =>
                            changeContact(i, { kind: e.target.value as "email" | "phone" })
                          }
                          inputProps={{ "aria-label": `Contact kind ${i + 1}` }}
                        >
                          <MenuItem value="email">email</MenuItem>
                          <MenuItem value="phone">phone</MenuItem>
                        </Select>
                      </FormControl>
                    </td>
                    <td>
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={SUBTYPE_SUGGESTIONS}
                        value={c.subtype}
                        onInputChange={(_e, val) => changeContact(i, { subtype: val })}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="personal, work, school…"
                            inputProps={{ ...params.inputProps, "aria-label": `Contact label ${i + 1}` }}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                    </td>
                    <td>
                      <IconButton size="small" onClick={() => removeContact(i)} title="Remove contact">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
        {contacts.some((c) => c.kind === "email") && (
          <Box sx={{ mb: 0.5 }}>
            <CoreSampleReadOnly label="Email sample" sample={sampleFor("email")} />
          </Box>
        )}
        {contacts.some((c) => c.kind === "phone") && (
          <Box sx={{ mb: 0.5 }}>
            <CoreSampleReadOnly label="Phone sample" sample={sampleFor("phone")} />
          </Box>
        )}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addContact}
          sx={{ textTransform: "none", color: "#845adf" }}
        >
          + Add contact
        </Button>
      </Paper>

      {/* Postal address — the column group that makes a mailable address (MPN-1706) */}
      <Paper variant="outlined" sx={sectionSx}>
        <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>Postal address</Typography>
        <Typography sx={{ color: "#8c9097", fontSize: 13, mb: 1.5 }}>
          Needed for direct mail, and only for direct mail. One row per address in the file:
          add a second row if the file carries a home and a campus address. Street, city, state
          and ZIP are what the postal service needs to find a house; a record missing one of
          them is still loaded and still served everywhere else, it just never gets mailed.
          Leave Country blank for US.
        </Typography>
        <FileColumnsNote file={fileColumns} />
        {addresses.length > 0 && (
          <Box sx={{ overflowX: "auto", mb: 1.5 }}>
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 640,
                tableLayout: "fixed",
                borderCollapse: "collapse",
                "& th": {
                  textAlign: "left",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#8c9097",
                  pb: 1,
                  pr: 2,
                  borderBottom: "1px solid #e9ecef",
                },
                "& td": { pt: 1.5, pr: 2, verticalAlign: "middle" },
              }}
            >
              <thead>
                <tr>
                  {ADDRESS_PARTS.map((part) => (
                    <th key={part.key}>
                      {part.label}
                      {part.required && <span style={{ color: "#dc2626" }}> *</span>}
                    </th>
                  ))}
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {addresses.map((a, i) => (
                  <tr key={i}>
                    {ADDRESS_PARTS.map((part) => (
                      <td key={part.key}>
                        <FileColumnInput
                          value={a[part.key]}
                          onChange={(v) => changeAddress(i, { [part.key]: v })}
                          header={fileColumns.columns}
                          placeholder={part.placeholder}
                          ariaLabel={`Address ${part.label} ${i + 1}`}
                        />
                      </td>
                    ))}
                    <td>
                      <IconButton size="small" onClick={() => removeAddress(i)} title="Remove address">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addAddress}
          sx={{ textTransform: "none", color: "#845adf" }}
        >
          + Add address
        </Button>
      </Paper>

      {/* Account (organization) — B2B match key + firmographics (MPN-1256) */}
      <Paper variant="outlined" sx={sectionSx}>
        <SectionHead
          title="Account (organization)"
          help="B2B only. Match each record to an existing organization by a key column, then map firmographics. Leave blank for B2C."
        />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start", mb: 2 }}>
          <TextField
            size="small"
            label="Match key column"
            value={orgKeyCol}
            onChange={(e) => setOrgKeyCol(e.target.value)}
            placeholder="e.g. account_id"
            sx={{ width: FIELD_W }}
          />
        </Box>
        <CoreFieldRows
          rows={orgRows}
          onChange={setOrgRows}
          targetOptions={accountOptions}
          metricTypes={metricTypes}
          fieldLabel="Firmographic field"
          addLabel="+ Add firmographic"
        />
      </Paper>

      {/* Person & consumer details -> identity.entries (MPN-1256) */}
      <Paper variant="outlined" sx={sectionSx}>
        <SectionHead
          title="Person & consumer details"
          help="Names, status and other attributes on the person/consumer record. Keyed off the record key above."
        />
        <CoreFieldRows
          rows={identityRows}
          onChange={setIdentityRows}
          targetOptions={personConsumerOptions}
          metricTypes={metricTypes}
          fieldLabel="Person/consumer field"
          addLabel="+ Add detail"
        />
      </Paper>

      {/* Sales pipeline -> core_load_specs sales_pipeline (SalesLead) (MPN-1256) */}
      <Paper variant="outlined" sx={sectionSx}>
        <SectionHead
          title="Sales pipeline"
          help="Downstream SalesLead attributes (e.g. stage). Keyed off the record key above."
        />
        <CoreFieldRows
          rows={salesLeadRows}
          onChange={setSalesLeadRows}
          targetOptions={salesLeadOptions}
          metricTypes={metricTypes}
          fieldLabel="SalesLead field"
          addLabel="+ Add field"
        />
      </Paper>

      {/* Holdout from file -> core_load_specs consumer_population_segment (MPN-1667) */}
      <Paper variant="outlined" sx={sectionSx}>
        <SectionHead
          title="Holdout"
          help="Off: the platform assigns the holdout from the Population page. On: the file decides — each member's first appearance reads the old-platform treatment column (Y = served, N = holdout). Changing this only affects members not yet seen."
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={holdoutFromFile}
              onChange={(e) => setHoldoutFromFile(e.target.checked)}
              disabled={!idCol.trim()}
            />
          }
          label={`Holdout comes from the file's "${TREATMENT_COLUMN}" column`}
        />
      </Paper>

      {/* Map fields */}
      <Paper variant="outlined" sx={sectionSx}>
        <SectionHead title="Map fields" help="One row per data column. Unmapped columns are ignored." />

        {metrics.length === 0 && (
          <Typography sx={{ color: "#8c9097", fontSize: 13, mb: 1 }}>
            Choose a vertical above to see its tracked fields.
          </Typography>
        )}

        {gridRows.length > 0 && (
          // Scroll instead of clip: below the table's min width the trash column would
          // otherwise be pushed off the Paper's edge.
          <Box sx={{ overflowX: "auto", mb: 1.5 }}>
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 620,
                tableLayout: "fixed",
                borderCollapse: "collapse",
                "& th": {
                  textAlign: "left",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#8c9097",
                  pb: 1,
                  pr: 2,
                  borderBottom: "1px solid #e9ecef",
                },
                "& td": { pt: 1, pb: 0.5, pr: 2, verticalAlign: "top" },
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: "26%" }}>Customer Column</th>
                  <th style={{ width: "34%" }}>Tracked Field Name</th>
                  <th>Dimension</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>{gridRows.map((row, index) => renderGridRow(row, index))}</tbody>
            </Box>
          </Box>
        )}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addGridRow}
          disabled={metrics.length === 0}
          sx={{ textTransform: "none", color: "#845adf" }}
        >
          + Add column
        </Button>
      </Paper>

      <ConfigVersionsDrawer
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        versions={versions}
        definitionName={definitionName}
      />
    </Box>
  );
}

// ---- Route wrapper ----

export default function FieldMapEditorPage() {
  useDocumentTitle("Field Map");
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const definitionId = Number(configId);

  return (
    <FieldMapEditor
      definitionId={definitionId}
      onBack={() => navigate(`/configs/${definitionId}/files`)}
      onOpenInstructions={() => navigate(`/configs/${definitionId}/instructions`)}
    />
  );
}
